/**
 * RCM-local formatting helpers.
 * Duplicated from RAO/utils/formatting.ts — see that file for the rule sources.
 */

export function maskSsn(ssn: string | undefined | null): string {
  if (!ssn) return '—';
  const digits = ssn.replace(/\D/g, '');
  if (digits.length < 4) return '***-**-****';
  return `***-**-${digits.slice(-4)}`;
}

export function formatIsoDate(iso: string | undefined | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

export function formatRelative(iso: string | undefined | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const diffMs = Date.now() - d.getTime();
  const diffMin = Math.round(diffMs / 60000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffH = Math.round(diffMin / 60);
  if (diffH < 24) return `${diffH}h ago`;
  const diffD = Math.round(diffH / 24);
  if (diffD < 30) return `${diffD}d ago`;
  return formatIsoDate(iso);
}

export function initials(name: string | undefined | null): string {
  if (!name) return '??';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 0 || !parts[0]) return '??';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  const last = parts[parts.length - 1];
  return ((parts[0][0] ?? '') + (last?.[0] ?? '')).toUpperCase();
}

/**
 * Relationship-code display labels — canonical to JHA's institution-configured
 * AcctRelCode catalog at PCB (inst 081501049 PROD). 38 active codes total:
 * 16 originally seeded + 22 added 2026-05-11 (Scott Bond MNTUSR adds).
 *
 * Source of truth: reference_jha_acctrelcode_catalog_20260510.md.
 * Verified live via /v3/config/core-director-codes?domain=AcctRelCode.
 *
 * Retired codes intentionally omitted: JT (joint via multiple AO rows),
 * FI (too generic — specific roles cover). Prior v3-invented codes
 * (CD/CS/PC/MI/MP/OB) removed — they were not in JHA's catalog and
 * displaying them as if they were created data-quality confusion.
 */
export const REL_CODE_LABELS: Record<string, string> = {
  // Personal / Joint / Beneficiary
  AO: 'Account Owner',
  AS: 'Authorized Signer',
  DS: 'Durable Authorized Signer',
  BP: 'POD Beneficiary (Primary)',
  BC: 'POD Beneficiary (Contingent)',
  C:  'Co-Signer',
  G:  'Guarantor',
  RE: 'Related',

  // Fiduciary / Custodial
  PR: 'Executor / Personal Representative',
  GN: 'Guardian / Conservator',
  RP: 'Representative Payee',
  PA: 'Public Administrator',
  PO: 'Power of Attorney',
  VA: 'VA Fiduciary',
  TR: 'Trustee',
  SG: 'Trust Settlor / Grantor',
  CU: 'Custodian (UTMA/UGMA)',
  SC: 'Successor Custodian',

  // Business — beneficial ownership / control (FinCEN CDD)
  BO: 'Beneficial Owner',
  CT: 'Beneficial Controller',
  CO: 'Controller & Beneficial Owner',

  // Business — officers
  OF: 'Officer',
  CE: 'Chief Executive Officer',
  PS: 'President',
  VP: 'Vice-President',
  OO: 'Chief Operating Officer',
  SE: 'Secretary',
  TS: 'Treasurer',
  CF: 'Chief Financial Officer',
  CH: 'Chairperson',
  DR: 'Director',

  // Business — structure
  LM: 'LLC Member',
  MG: 'Manager (LLC)',
  MM: 'Managing Member (LLC)',
  PT: 'Partner',
  RA: 'Registered Agent',
  PI: 'Principal',
  GA: 'Grant Administrator',
};
